import React, { useState } from 'react';
import notify from 'devextreme/ui/notify';
import { beginColumnResize, columnPixelWidth } from '../utils/gridColumnLayout';
import './GridColumnLayoutBar.css';

const AUDIENCES = [
  { id: 'admin', label: 'Yöneticiler' },
  { id: 'staff', label: 'Yönetici olmayanlar' },
];

export function GridColumnTitle({
  column,
  extra = null,
  canManage = false,
  onResize,
  onDropColumn,
}) {
  const id = column._layoutId;
  return (
    <div
      className="grid-col-title"
      onDragOver={canManage ? (event) => {
        event.preventDefault();
        event.stopPropagation();
      } : undefined}
      onDrop={canManage ? (event) => {
        event.preventDefault();
        event.stopPropagation();
        const from = event.dataTransfer.getData('text/plain');
        if (from && from !== id) onDropColumn?.(from, id);
      } : undefined}
    >
      {canManage && (
        <span
          className="grid-col-grip"
          draggable
          title="Kolonu taşı"
          aria-hidden="true"
          onMouseDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          onDragStart={(event) => {
            event.stopPropagation();
            event.dataTransfer.setData('text/plain', id);
            event.dataTransfer.effectAllowed = 'move';
          }}
        >
          ⋮⋮
        </span>
      )}
      <span className="grid-col-title__label">
        {column.caption || column.dataField}
        {extra}
      </span>
      {canManage && (
        <span
          className="grid-col-resizer"
          title="Genişliği değiştir"
          onMouseDown={(event) => beginColumnResize(event, column, onResize)}
          onClick={(event) => event.stopPropagation()}
        />
      )}
    </div>
  );
}

export function GridColumnLayoutBar({
  enabled,
  columns,
  onMoveBy,
  onToggle,
  onWidth,
  onSave,
  onReset,
}) {
  const [open, setOpen] = useState(false);
  const [audience, setAudience] = useState('admin');
  const [busy, setBusy] = useState(false);
  if (!enabled) return null;

  const run = async (action, successText) => {
    setBusy(true);
    try {
      await action();
      const label = AUDIENCES.find((item) => item.id === audience)?.label || audience;
      notify(`${successText} (${label})`, 'success', 1600);
    } catch (err) {
      const msg = err?.response?.data?.errmsg || err?.message || String(err);
      notify(msg, 'error', 2500);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid-layout-bar">
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        Kolonlar
      </button>
      {open && (
        <div className="grid-layout-panel" role="dialog" aria-label="Kolon düzeni">
          <div className="grid-layout-panel__audience">
            {AUDIENCES.map((item) => (
              <label key={item.id}>
                <input
                  type="radio"
                  name="grid-layout-audience"
                  checked={audience === item.id}
                  onChange={() => setAudience(item.id)}
                />
                {item.label}
              </label>
            ))}
          </div>
          <p className="grid-layout-panel__hint">
            Sırayı sürükleyin veya oklarla değiştirin, kenardan genişliği ayarlayın, gizlenen kolonu yeniden işaretleyin.
            Ekrandaki düzen seçilen gruba kaydedilir.
          </p>
          <ul className="grid-layout-panel__list">
            {columns.map((col, index) => (
              <li key={col._layoutId}>
                <input
                  type="checkbox"
                  checked={col.visible !== false}
                  aria-label={`${col.caption || col.dataField} görünür`}
                  onChange={(event) => onToggle(col._layoutId, event.target.checked)}
                />
                <span className="grid-layout-panel__name">{col.caption || col.dataField}</span>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  min={48}
                  max={800}
                  aria-label={`${col.caption || col.dataField} genişlik`}
                  value={columnPixelWidth(col) || 120}
                  onChange={(event) => onWidth(col._layoutId, Number(event.target.value))}
                />
                <button type="button" className="btn btn-sm btn-light" disabled={index === 0} onClick={() => onMoveBy(col._layoutId, -1)} aria-label="Yukarı">↑</button>
                <button type="button" className="btn btn-sm btn-light" disabled={index === columns.length - 1} onClick={() => onMoveBy(col._layoutId, 1)} aria-label="Aşağı">↓</button>
              </li>
            ))}
          </ul>
          <div className="grid-layout-panel__actions">
            <button type="button" className="btn btn-sm btn-primary" disabled={busy} onClick={() => run(() => onSave(audience), 'Kolon düzeni kaydedildi')}>
              Bu düzeni kaydet
            </button>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled={busy} onClick={() => run(() => onReset(audience), 'Kolon düzeni varsayılana döndü')}>
              Grubu varsayılana döndür
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
